import { useEffect, useState, useRef } from "react";
import { CheckCircle2, AlertCircle, Loader2, Search } from "lucide-react";
import { INDIAN_BANKS, OTHER_BANK, normaliseBankName } from "@/constants/banks";

/**
 * Shared bank-details fieldset used by Register, KYC and standalone Bank pages.
 *
 * UX:
 *  - Bank Name = searchable dropdown of 60+ Indian banks + "Other" fallback.
 *  - IFSC     = text input that live-verifies against Razorpay's free public
 *               IFSC API (https://ifsc.razorpay.com/{IFSC}). On a valid IFSC:
 *                 • Bank Name auto-selects if the API's bank matches our list.
 *                 • Branch auto-fills with the API's branch value.
 *                 • Green tick shown next to IFSC.
 *  - Account Holder / Account Number = plain inputs.
 *
 * Props: `values` (dict), `onChange` (setter), `required` (bool),
 *        `disabled` (bool), `compact` (bool — tighter typography for Register).
 */
export default function BankFields({
  values,
  onChange,
  required = false,
  disabled = false,
  compact = false,
  testidPrefix = "bank",
}) {
  const [bankSearch, setBankSearch] = useState("");
  const [bankOpen, setBankOpen]     = useState(false);
  const [ifscState, setIfscState]   = useState({ status: "idle", info: null });
  const bankRef                     = useRef(null);

  const set = (k, v) => onChange({ ...values, [k]: v });

  // Close bank dropdown on outside click
  useEffect(() => {
    const h = (e) => { if (bankRef.current && !bankRef.current.contains(e.target)) setBankOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Debounced IFSC verification via Razorpay's public API
  useEffect(() => {
    const raw = (values.ifsc || "").trim().toUpperCase();
    if (!raw) { setIfscState({ status: "idle", info: null }); return; }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(raw)) {
      setIfscState({ status: "invalid", info: { error: "Format ABCD0XXXXXX" } });
      return;
    }
    let cancelled = false;
    setIfscState({ status: "loading", info: null });
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`https://ifsc.razorpay.com/${raw}`);
        if (!r.ok) throw new Error("Unknown IFSC");
        const data = await r.json();
        if (cancelled) return;
        setIfscState({ status: "ok", info: data });
        // Auto-fill bank + branch only if user hasn't already typed them
        const nb = normaliseBankName(data.BANK || "");
        const next = { ...values };
        if (!values.bank_name)              next.bank_name = INDIAN_BANKS.includes(nb) ? nb : OTHER_BANK;
        if (!values.bank_name_other && nb && !INDIAN_BANKS.includes(nb))
          next.bank_name_other = nb;
        if (!values.branch && data.BRANCH)  next.branch = data.BRANCH;
        onChange(next);
      } catch (e) {
        if (cancelled) return;
        setIfscState({ status: "invalid", info: { error: "IFSC not found" } });
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.ifsc]);

  const filteredBanks = bankSearch
    ? INDIAN_BANKS.filter((b) => b.toLowerCase().includes(bankSearch.toLowerCase()))
    : INDIAN_BANKS;

  const labelCls = compact
    ? "text-[10px] uppercase tracking-[0.3em] text-gold-deep"
    : "text-xs uppercase tracking-widest text-navy/60";
  const inputCls = "mt-1 w-full border border-border px-3 py-2.5 text-sm rounded-sm focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 transition";

  const selectedBank = values.bank_name || "";
  const isOther      = selectedBank === OTHER_BANK;

  return (
    <div className="space-y-4">
      {/* Account holder */}
      <div>
        <label className={labelCls}>Account Holder Name {required && <span className="text-rose">*</span>}</label>
        <input
          data-testid={`${testidPrefix}-holder`}
          value={values.account_holder || ""}
          disabled={disabled}
          required={required}
          onChange={(e) => set("account_holder", e.target.value)}
          placeholder="As printed on the passbook"
          className={inputCls}
        />
      </div>

      {/* Bank name — searchable dropdown */}
      <div ref={bankRef} className="relative">
        <label className={labelCls}>Bank Name {required && <span className="text-rose">*</span>}</label>
        <button
          type="button"
          data-testid={`${testidPrefix}-name-toggle`}
          onClick={() => setBankOpen((o) => !o)}
          disabled={disabled}
          className={`${inputCls} text-left flex items-center justify-between ${!selectedBank ? "text-navy/40" : "text-navy"}`}
        >
          <span className="truncate">{selectedBank || "Select your bank…"}</span>
          <span className="text-navy/40 text-xs ml-2 shrink-0">▾</span>
        </button>
        {bankOpen && (
          <div className="absolute z-30 mt-1 w-full max-h-72 overflow-hidden bg-white border border-line rounded-sm shadow-lg">
            <div className="p-2 border-b border-line/70 bg-ivory/60 flex items-center gap-2">
              <Search size={12} className="text-navy/40" />
              <input
                autoFocus
                value={bankSearch}
                onChange={(e) => setBankSearch(e.target.value)}
                placeholder="Search bank…"
                data-testid={`${testidPrefix}-name-search`}
                className="w-full bg-transparent text-sm outline-none"
              />
            </div>
            <div className="max-h-56 overflow-y-auto py-1">
              {filteredBanks.length === 0 && (
                <div className="px-3 py-6 text-xs text-navy/50 text-center">No banks match "{bankSearch}"</div>
              )}
              {filteredBanks.map((b) => (
                <button
                  type="button"
                  key={b}
                  data-testid={`${testidPrefix}-name-opt-${b.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => { set("bank_name", b); setBankOpen(false); setBankSearch(""); }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-cream/60 ${b === selectedBank ? "bg-gold/[0.08] text-royal font-medium" : "text-navy"}`}
                >
                  {b}
                </button>
              ))}
              <div className="border-t border-line/60 mt-1">
                <button
                  type="button"
                  data-testid={`${testidPrefix}-name-other`}
                  onClick={() => { set("bank_name", OTHER_BANK); setBankOpen(false); setBankSearch(""); }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-cream/60 ${isOther ? "bg-gold/[0.08] text-royal font-medium" : "text-gold-deep"}`}
                >
                  {OTHER_BANK}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Free-text bank when "Other" */}
      {isOther && (
        <div>
          <label className={labelCls}>Bank Name (specify)</label>
          <input
            data-testid={`${testidPrefix}-name-manual`}
            value={values.bank_name_other || ""}
            disabled={disabled}
            onChange={(e) => set("bank_name_other", e.target.value)}
            placeholder="Enter bank name exactly as on passbook"
            className={inputCls}
          />
        </div>
      )}

      {/* IFSC — with live lookup */}
      <div>
        <div className="flex items-center justify-between">
          <label className={labelCls}>IFSC Code {required && <span className="text-rose">*</span>}</label>
          {ifscState.status === "loading" && <Loader2 size={12} className="text-gold animate-spin" />}
          {ifscState.status === "ok"      && <CheckCircle2 size={12} className="text-olive" />}
          {ifscState.status === "invalid" && <AlertCircle  size={12} className="text-rose" />}
        </div>
        <input
          data-testid={`${testidPrefix}-ifsc`}
          value={values.ifsc || ""}
          disabled={disabled}
          required={required}
          spellCheck={false}
          onChange={(e) => set("ifsc", e.target.value.toUpperCase())}
          placeholder="HDFC0001234"
          className={`${inputCls} font-mono tracking-wider ${
            ifscState.status === "ok"      ? "border-olive/60 focus:border-olive focus:ring-olive/20" :
            ifscState.status === "invalid" ? "border-rose/60  focus:border-rose  focus:ring-rose/20"  : ""
          }`}
        />
        {ifscState.status === "ok" && ifscState.info && (
          <div data-testid={`${testidPrefix}-ifsc-verified`} className="mt-1 text-[11px] text-olive leading-snug">
            ✓ {ifscState.info.BANK} · {ifscState.info.BRANCH} · {ifscState.info.CITY}, {ifscState.info.STATE}
          </div>
        )}
        {ifscState.status === "invalid" && ifscState.info?.error && (
          <div className="mt-1 text-[11px] text-rose leading-snug">! {ifscState.info.error}</div>
        )}
        {ifscState.status === "idle" && (
          <div className="mt-1 text-[11px] text-navy/50 leading-snug">Format: ABCD0XXXXXX — bank + branch auto-fill when valid.</div>
        )}
      </div>

      {/* Account number */}
      <div>
        <label className={labelCls}>Account Number {required && <span className="text-rose">*</span>}</label>
        <input
          data-testid={`${testidPrefix}-account`}
          value={values.bank_account || ""}
          disabled={disabled}
          required={required}
          onChange={(e) => set("bank_account", e.target.value.replace(/\s+/g, ""))}
          placeholder="1234 5678 9012"
          className={`${inputCls} font-mono tracking-wider`}
        />
      </div>

      {/* Branch (auto-filled by IFSC lookup, editable) */}
      <div>
        <label className={labelCls}>Branch</label>
        <input
          data-testid={`${testidPrefix}-branch`}
          value={values.branch || ""}
          disabled={disabled}
          onChange={(e) => set("branch", e.target.value)}
          placeholder="Auto-filled from IFSC lookup"
          className={inputCls}
        />
      </div>
    </div>
  );
}
